import { useQuery } from '@tanstack/react-query';
import {
  generateLearningModule,
  generateMarketInsights,
  generateFAQAnswers,
  generateTradingBasics,
  generateSafetyTips,
  generateCommonScams,
  generateProgressInsights
} from '../services/openai';

export function useLearningModule(topic: string, level: 'beginner' | 'intermediate' | 'advanced', enabled: boolean = true) {
  return useQuery({
    queryKey: ['learningModule', topic, level],
    queryFn: () => generateLearningModule(topic, level),
    enabled,
    staleTime: 86400000,
    gcTime: 172800000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

export function useMarketInsights(marketData?: any) {
  return useQuery({
    queryKey: ['marketInsights', marketData],
    queryFn: () => generateMarketInsights(marketData),
    enabled: !!marketData,
    staleTime: 1800000,
    gcTime: 3600000,
    retry: 2,
  });
}

export function useFAQAnswer(question: string, enabled: boolean = false) {
  return useQuery({
    queryKey: ['faqAnswer', question],
    queryFn: () => generateFAQAnswers(question),
    enabled,
    staleTime: 86400000,
    gcTime: 172800000,
    retry: 2,
  });
}

export function useTradingBasics(marketContext?: any) {
  return useQuery({
    queryKey: ['tradingBasics', marketContext],
    queryFn: () => generateTradingBasics(marketContext),
    staleTime: 3600000,
    gcTime: 7200000,
    retry: 2,
  });
}

export function useSafetyTips(threats?: string[]) {
  return useQuery({
    queryKey: ['safetyTips', threats],
    queryFn: () => generateSafetyTips(threats),
    staleTime: 3600000,
    gcTime: 7200000,
    retry: 2,
  });
}

export function useCommonScams() {
  return useQuery({
    queryKey: ['commonScams'],
    queryFn: () => generateCommonScams(),
    staleTime: 3600000,
    gcTime: 7200000,
    retry: 2,
  });
}

export function useProgressInsights(userData: {
  completedModules: number;
  totalModules: number;
  studyTime: number;
  currentStreak: number;
}, enabled: boolean = false) {
  return useQuery({
    queryKey: ['progressInsights', userData],
    queryFn: () => generateProgressInsights(userData),
    enabled,
    staleTime: 1800000,
    gcTime: 3600000,
    retry: 2,
  });
}
